import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ICompanySettings } from '@/modules/settings/company/types';
import axiosClient from '@/shared/lib/axios';
import { IGeneral } from '@/shared/types';

//********************** View and edit general company data **********************/

//  get company details
const getCompanyDetails = async (
  companyId?: number
): Promise<ICompanySettings.ICompanyDetails> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/settings/company/${companyId}`
  );
  return data;
};
export const useGetCompanyDetails = (companyId?: number) => {
  return useQuery({
    queryKey: ['getCompanyDetails', companyId],
    queryFn: () => getCompanyDetails(companyId),
    enabled: !!companyId,
  });
};

//  edit company details
const editCompanyDetails = async ({
  companyId,
  formData,
}: ICompanySettings.IUpdateCompanySchema): Promise<ICompanySettings.ICompanyDetails> => {
  const { data } = await axiosClient.patch(
    `/company/portalInsights/settings/company/${companyId}`,
    formData
  );
  return data;
};
export const useEditCompanyDetails = () => {
  return useMutation({
    mutationFn: editCompanyDetails,
  });
};

//********************** Company Properties **********************/

//  Get Properties List
const getProperties = async (
  params: ICompanySettings.IParams
): Promise<ICompanySettings.IGetPropertiesResponse> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/settings/company/property`,
    { params }
  );
  return data;
};
export const useGetProperties = (params: ICompanySettings.IParams) => {
  return useQuery({
    queryKey: ['getProperties', params],
    queryFn: () => getProperties(params),
    enabled: !!params,
  });
};

//  Create new property
const createNewProperty = async (
  values: ICompanySettings.IAddPropertyFormDTO
): Promise<{ entityId: number }> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/settings/company/property`,
    values
  );
  return data;
};
export const useCreateNewProperty = () => {
  return useMutation({
    mutationFn: createNewProperty,
  });
};

//********************** Company Crops **********************/

//  Get company crops
const getCompanyCrops = async (params: ICompanySettings.IParams) => {
  const { data } =
    await axiosClient.get<ICompanySettings.IGetCompanyCropsResponse>(
      `/company/portalInsights/settings/company/crops`,
      { params }
    );
  //  Transorm the schema to the one used by the infinite select field component
  return {
    items: data.companyCrops,
    currentPage: params.pageNumber,
    totalPages: Math.ceil(data.totalCount / params.pageSize),
    totalItems: data.totalCount,
    nextPage:
      params?.pageNumber * params?.pageSize < data?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetCompanyCrops = ({
  companyId,
  pageNumber,
  pageSize,
}: ICompanySettings.IParams) => {
  //  useInfiniteQuery because we need infinite scroll in the select field, so we handle pagination differently in the table
  return useInfiniteQuery({
    queryKey: ['getCompanyCrops', companyId, { pageNumber, pageSize }],
    queryFn: ({ pageParam }) =>
      getCompanyCrops({
        companyId,
        pageNumber: pageParam ?? pageNumber,
        pageSize,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!companyId && !!pageNumber && !!pageSize,
  });
};

//  Create new company crop
const createCompanyCrop = async (
  cropData: ICompanySettings.ICreateCompanyCropDTO
): Promise<IGeneral.ICropSchema[]> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/settings/company/crops`,
    cropData
  );
  return data;
};
export const useCreateCompanyCrop = () => {
  return useMutation({
    mutationFn: createCompanyCrop,
  });
};

//********************** Company SUppliers **********************/

//  Get suppliers
export const useGetCompanySuppliers = (
  params?: ICompanySettings.IGetSuppliersQueryParams
) => {
  const getSupplier =
    async (): Promise<ICompanySettings.IGetSuppliersResponse> => {
      const response = await axiosClient.get(
        `/inventory/api/v1/products-suppliers`,
        {
          params,
        }
      );
      return response.data;
    };
  return useQuery({
    queryKey: ['suppliersResourceManagement', params],
    queryFn: getSupplier,
  });
};

// get archived suppliers
const getArchivedSuppliers = async (
  params: IGeneral.IQueryParams
): Promise<ICompanySettings.IGetArchivedSupliersResponse> => {
  const { data } = await axiosClient.get(
    '/inventory/api/v1/products-suppliers/inactive',
    { params }
  );
  return data;
};

export const useGetArchivedSuppliers = (params: IGeneral.IQueryParams) => {
  return useQuery({
    queryKey: ['getArchivedSuppliers', params],
    queryFn: () => getArchivedSuppliers(params),
  });
};

//  Add suppliers
const addEditSupllier = async ({
  data,
  id,
}: {
  data: ICompanySettings.ISupplierInfo;
  id?: number;
}) => {
  const response = await axiosClient[id ? 'put' : 'post'](
    `/inventory/api/v1/products-suppliers${id ? `/${id}` : ''}`,
    data
  );
  return response.data;
};

export const useAddEditSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addEditSupllier,
    onSuccess: () => {
      queryClient.invalidateQueries(['suppliersResourceManagement']);
    },
  });
};

// Archive suppliers
const archiveSupplier = async (id: number) => {
  const { data } = await axiosClient.patch(
    `/inventory/api/v1/products-suppliers/${id}`
  );
  return data;
};

export const useArchiveSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: archiveSupplier,
    onSuccess: () => {
      queryClient.invalidateQueries(['suppliersResourceManagement']);
    },
  });
};

//********************** Inputs **********************/

// comapny custom input list
const getCompanyCustomInputs = async ({
  companyId,
  entityId,
  inputType,
  ...params
}: ICompanySettings.IGetCompanyInputsParams): Promise<ICompanySettings.GetCompanyInputsResponse> => {
  const url =
    inputType === 'country'
      ? `/inventory/api/v1/warehouses-stock/company/${companyId}/entity/${entityId}/products`
      : `/inventory/api/v1/products/company/custom-input`;
  const { data } = await axiosClient.get(url, { params });
  return data;
};

export const useGetCompanyCustomInputs = (
  params: ICompanySettings.IGetCompanyInputsParams
) => {
  return useQuery({
    queryKey: ['getCompanyInputs', params],
    queryFn: () => getCompanyCustomInputs(params),
    enabled:
      params.inputType === 'custom' ||
      (params.inputType === 'country' &&
        !!params.companyId &&
        !!params.entityId),
  });
};

// Get countries of company's entities
const getEntitiesCountries = async (
  companyId: number | undefined
): Promise<ICompanySettings.IEntityCountry[]> => {
  const { data } = await axiosClient.get(
    `/inventory/api/v1/entities/company/${companyId}/countries`
  );
  return data;
};

export const useGetEntitiesCountries = (companyId: number | undefined) => {
  return useQuery({
    queryKey: ['getEntitiesCountries', companyId],
    queryFn: () => getEntitiesCountries(companyId),
    enabled: !!companyId,
  });
};

// Get active substances
const getActiveSubstances = async ({
  countryId,
  ...params
}: ICompanySettings.IGetActiveSubstancesParams) => {
  const { data } = await axiosClient.get<
    IGeneral.ISuccessResponseDataSchema<IGeneral.NameIdPair>
  >(`/inventory/api/v2/active-substances/${countryId}`, { params });
  return {
    ...data,
    nextPage:
      params?.page && params.page < data.totalPages
        ? params.page + 1
        : undefined,
  };
};

export const useGetActiveSubstances = (
  params: ICompanySettings.IGetActiveSubstancesParams
) => {
  return useInfiniteQuery({
    queryKey: ['getActiveSubstances', params],
    queryFn: ({ pageParam }) =>
      getActiveSubstances({ ...params, page: pageParam ?? params.page }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!params?.countryId && !!params?.page && !!params?.limit,
  });
};

// Create custom input
const createEditCustomInput = async ({
  obj,
  id,
}: {
  obj: ICompanySettings.ICreateInputDTO;
  id?: number;
}) => {
  const { data } = await axiosClient[id ? 'put' : 'post'](
    `/inventory/api/v1/products/custom-input${id ? `/${id}` : ''}`,
    obj
  );
  return data;
};

export const useCreateEditCustomInput = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEditCustomInput,
    onSuccess: () => {
      queryClient.invalidateQueries(['getCompanyInputs']);
      queryClient.invalidateQueries(['getProductsList']);
    },
  });
};

/*************************************** INACTIVE TEAM MEMBERS ****************************************** */

export const getInactiveTeamMembers = async (
  params: ICompanySettings.IGetInactiveTeamMembersSchema
): Promise<ICompanySettings.IGetInactiveTeamMembers> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/team-members/archived`,
    {
      params,
    }
  );
  return data;
};
export const useGetInactiveTeamMembers = (
  params: ICompanySettings.IGetInactiveTeamMembersSchema
) => {
  return useQuery(['getInactiveTeamMember', params], () =>
    getInactiveTeamMembers(params)
  );
};
